// src/useNewVersionPrompt.js
import { useContext, useEffect, useRef, useState } from "react";
import MainContext from "../MainContext";

const SHOWN_KEY = "appVersionShown"; // ultima versione per cui il popup è stato visto

export function useNewVersionPrompt(intervalMs = 30000) {
  const [baseVersion, setBaseVersion] = useState(null); 
  const [updateInfo, setUpdateInfo] = useState(null);   
  const [isFirstView, setIsFirstView] = useState(false); // nuovo flag
  const timer = useRef(null);

  const context = useContext(MainContext);
  const isLoggedIn = !!context?.user && !context.user?.isAnonymous;

  const isWithinShowWindow = (remote) => {
    if (!remote?.show_until) return true;
    const untilMs = Date.parse(remote.show_until);
    if (Number.isNaN(untilMs)) return true;
    return Date.now() <= untilMs;
  };

  const fetchVersion = async () => {
    const res = await fetch("/version.json", { cache: "reload" });
    if (!res.ok) return null;
    try { return await res.json(); } catch { return null; }
  };

  useEffect(() => {
    if (!isLoggedIn) {
      setUpdateInfo(null);
      setIsFirstView(false);
      return;
    }

    let stopped = false;

    const initial = async () => {
      const remote = await fetchVersion();
      if (!remote?.version) return;

      const canShowDialog = isWithinShowWindow(remote);
      const alreadyShown = localStorage.getItem(SHOWN_KEY);

      if (canShowDialog && remote.version !== alreadyShown) {
        // primo arrivo su questa versione
        setUpdateInfo(remote);
        setIsFirstView(true); // differenzia
        localStorage.setItem(SHOWN_KEY, remote.version);
      } else if (!canShowDialog) {
        setUpdateInfo(null);
        setIsFirstView(false);
      }

      setBaseVersion(remote.version);
      context?.setAppVersion?.(remote.version);
    };

    const poll = async () => {
      const remote = await fetchVersion();
      if (!remote?.version) {
        if (!stopped) timer.current = setTimeout(poll, intervalMs);
        return;
      }

      const canShowDialog = isWithinShowWindow(remote);
      if (!canShowDialog) {
        setUpdateInfo(null);
        setIsFirstView(false);
      }

      if (remote.version && baseVersion && remote.version !== baseVersion) {
        const alreadyShown = localStorage.getItem(SHOWN_KEY);

        if (canShowDialog && remote.version !== alreadyShown) {
          // cambio versione durante la sessione
          setUpdateInfo(remote);
          setIsFirstView(false); // non è "prima visita", ma "aggiornamento live"
          localStorage.setItem(SHOWN_KEY, remote.version);
        }

        setBaseVersion(remote.version);
        context?.setAppVersion?.(remote.version);
      }
      if (!stopped) timer.current = setTimeout(poll, intervalMs);
    };

    initial().then(() => {
      if (!stopped) timer.current = setTimeout(poll, intervalMs);
    });

    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      stopped = true;
      if (timer.current) clearTimeout(timer.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [intervalMs, baseVersion, context, isLoggedIn]);

  const reload = () => {
    window.location.reload();
  };

  return { updateInfo, isFirstView, reload };
}